import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import {
  archivadas,
  cumplidas,
  enCurso,
} from '#components/mobile/budget/recuerdos';
import type { Recuerdo } from '#components/mobile/budget/recuerdos';

/**
 * «Tu bitácora» (F1 §2, «La colección: el escritorio del armador»): lógica
 * pura del cuaderno, sin React. Una bitácora por barco (presupuesto abierto).
 *
 * - Repisa: las botellas abiertas (travesías cumplidas), de la primera que
 *   llegó a la última, en filas que crecen.
 * - Cuaderno: «Hoja de totales», una página por travesía cumplida (orden de
 *   llegada, n.º 1 la primera) y «Navegando» con las que siguen en curso.
 * - Cajón «Archivadas»: aparte, plegado, sin contador.
 *
 * Nada de huecos, rachas ni rankings: solo lo que ya ha pasado.
 */

/** Travesía cumplida con su número de orden en el cuaderno. */
export type Cumplida = {
  recuerdo: Recuerdo;
  /** N.º de travesía (1 = la primera que llegó). */
  numero: number;
  /** `AAAA-MM-DD` de la llegada. */
  llegada: string;
  /** Meta cumplida (la de la llegada: si se ajustó, la ajustada). */
  importe: IntegerAmount;
  /** Días de mar (zarpe → llegada, ≥ 0). */
  dias: number;
  /** Meses de mar redondeados (para «Duración»); 0 si < ~2 semanas. */
  meses: number;
  aportaciones: number | null;
  /** Importe / aportaciones (redondeado); null sin aportaciones. */
  aportacionMedia: IntegerAmount | null;
};

export type Totales = {
  numero: number;
  /** Suma de las metas cumplidas. */
  total: IntegerAmount;
  /** La de más días de mar (a igualdad, la primera). */
  masLarga: Cumplida | null;
  /** La primera que llegó. */
  primera: Cumplida | null;
};

export type PaginaCuaderno =
  | { tipo: 'totales' }
  | { tipo: 'vacia' }
  | { tipo: 'travesia'; cumplida: Cumplida }
  | { tipo: 'navegando' };

export type Cuaderno = {
  cumplidas: Cumplida[];
  totales: Totales;
  /** Las que navegan (sin llegar), la que zarpó antes primero. */
  navegando: Recuerdo[];
  /** Cerradas sin cumplir y guardadas en el cajón, la más reciente primero. */
  archivadas: Recuerdo[];
  paginas: PaginaCuaderno[];
};

/** Días de mar entre dos fechas `AAAA-MM-DD` (nunca negativo). */
export function diasDeMar(desde: string, hasta: string): number {
  return Math.max(0, monthUtils.differenceInCalendarDays(hasta, desde));
}

/** Meses de mar para la ficha: días / 30,44 redondeado. */
export function mesesDeMar(dias: number): number {
  return Math.round(dias / 30.44);
}

function aCumplida(r: Recuerdo, numero: number): Cumplida | null {
  const l = r.llegada;
  if (!l) {
    return null;
  }
  const dias = diasDeMar(r.base.zarpoEn, l.fecha);
  const aportaciones = l.aportaciones;
  return {
    recuerdo: r,
    numero,
    llegada: l.fecha,
    importe: l.meta,
    dias,
    meses: mesesDeMar(dias),
    aportaciones,
    aportacionMedia:
      aportaciones && aportaciones > 0
        ? Math.round(l.meta / aportaciones)
        : null,
  };
}

function porLlegada(a: Recuerdo, b: Recuerdo): number {
  const fa = a.llegada?.fecha ?? '';
  const fb = b.llegada?.fecha ?? '';
  if (fa !== fb) {
    return fa < fb ? -1 : 1;
  }
  if (a.base.zarpoEn !== b.base.zarpoEn) {
    return a.base.zarpoEn < b.base.zarpoEn ? -1 : 1;
  }
  return a.base.rid.localeCompare(b.base.rid);
}

/** Cumplidas numeradas por orden de llegada (n.º 1 la primera). */
export function numerarCumplidas(recuerdos: readonly Recuerdo[]): Cumplida[] {
  return cumplidas(recuerdos)
    .filter(r => r.base.estado !== 'devuelta')
    .sort(porLlegada)
    .map((r, i) => aCumplida(r, i + 1))
    .filter((c): c is Cumplida => c != null);
}

export function totalesDe(lista: readonly Cumplida[]): Totales {
  let masLarga: Cumplida | null = null;
  let total = 0;
  for (const c of lista) {
    total += c.importe;
    if (!masLarga || c.dias > masLarga.dias) {
      masLarga = c;
    }
  }
  return { numero: lista.length, total, masLarga, primera: lista[0] ?? null };
}

/** Todo el cuaderno de un barco a partir de sus recuerdos. */
export function cuadernoDe(
  recuerdos: readonly Recuerdo[],
  /** Categorías ocultas o borradas: sus travesías en curso no se ven. */
  ocultas: ReadonlySet<string> = new Set(),
): Cuaderno {
  const lista = numerarCumplidas(recuerdos);
  const navegando = [
    ...enCurso(recuerdos).filter(r => !ocultas.has(r.base.categoriaId)),
  ].sort((a, b) =>
    a.base.zarpoEn === b.base.zarpoEn
      ? a.base.rid.localeCompare(b.base.rid)
      : a.base.zarpoEn < b.base.zarpoEn
        ? -1
        : 1,
  );
  const cajon = [...archivadas(recuerdos)].sort((a, b) => {
    const fa = a.base.cerradaEn ?? a.base.zarpoEn;
    const fb = b.base.cerradaEn ?? b.base.zarpoEn;
    return fa === fb ? a.base.rid.localeCompare(b.base.rid) : fa < fb ? 1 : -1;
  });
  const paginas: PaginaCuaderno[] = [
    lista.length > 0 ? { tipo: 'totales' } : { tipo: 'vacia' },
    ...lista.map(cumplida => ({ tipo: 'travesia' as const, cumplida })),
    ...(navegando.length > 0 ? [{ tipo: 'navegando' as const }] : []),
  ];
  return {
    cumplidas: lista,
    totales: totalesDe(lista),
    navegando,
    archivadas: cajon,
    paginas,
  };
}

/** Página del cuaderno de una travesía (por rid); 0 (la primera) si no está. */
export function paginaDeRid(
  paginas: readonly PaginaCuaderno[],
  rid: string | null | undefined,
): number {
  if (!rid) {
    return 0;
  }
  const i = paginas.findIndex(
    p => p.tipo === 'travesia' && p.cumplida.recuerdo.base.rid === rid,
  );
  return Math.max(0, i);
}

/** Índice válido dentro del cuaderno (para pasar páginas sin salirse). */
export function acotarPagina(indice: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(total - 1, Math.trunc(indice)));
}

/** Nombre con su emoji, como se copió al zarpar. */
export function nombreDe(r: Recuerdo): string {
  return [r.base.emoji, r.base.nombre].filter(Boolean).join(' ');
}
